// User types
export var Gender;
(function (Gender) {
    Gender["MALE"] = "male";
    Gender["FEMALE"] = "female";
    Gender["OTHER"] = "other";
})(Gender || (Gender = {}));
export var UserRole;
(function (UserRole) {
    UserRole["PLAYER"] = "player";
    UserRole["TEAM_CAPTAIN"] = "teamCaptain";
    UserRole["LEAGUE_ADMINISTRATOR"] = "leagueAdministrator";
    UserRole["SUPER_ADMINISTRATOR"] = "superAdministrator";
})(UserRole || (UserRole = {}));
// Team types
export var TeamType;
(function (TeamType) {
    TeamType["SINGLES"] = "singles";
    TeamType["DOUBLES"] = "doubles";
    TeamType["MIXED_DOUBLES"] = "mixedDoubles";
})(TeamType || (TeamType = {}));
/** Role of a member within a team. The captain is always the creator. */
export var TeamMemberRole;
(function (TeamMemberRole) {
    TeamMemberRole["CAPTAIN"] = "captain";
    TeamMemberRole["MEMBER"] = "member";
})(TeamMemberRole || (TeamMemberRole = {}));
/**
 * Acceptance state of a member's place on a team.
 * The creator (captain) is always `accepted`. A named doubles/mixed partner
 * starts as `pending` until they accept or decline the partnership request.
 */
export var PartnershipStatus;
(function (PartnershipStatus) {
    PartnershipStatus["ACCEPTED"] = "accepted";
    PartnershipStatus["PENDING"] = "pending";
    PartnershipStatus["DECLINED"] = "declined";
})(PartnershipStatus || (PartnershipStatus = {}));
/**
 * Lifecycle status of a team.
 * - `pending_partner`: a doubles/mixed team awaiting the named partner's acceptance
 * - `active`: a singles team, or a pair team whose partner accepted
 * - `withdrawn`: the creator withdrew the team from competition
 * - `cancelled`: the partner declined, or the partner request expired
 * - `deleted`: the team was permanently deleted by its creator or a Super Admin.
 *   Only set on teams that carry game/enrollment history (a team with no history is
 *   hard-deleted instead); the record is retained so historical standings keep the
 *   team's name, but it is hidden from all active views. Distinct from `withdrawn`.
 */
export var TeamStatus;
(function (TeamStatus) {
    TeamStatus["PENDING_PARTNER"] = "pending_partner";
    TeamStatus["ACTIVE"] = "active";
    TeamStatus["WITHDRAWN"] = "withdrawn";
    TeamStatus["CANCELLED"] = "cancelled";
    TeamStatus["DELETED"] = "deleted";
})(TeamStatus || (TeamStatus = {}));
/**
 * Registration/approval status of a team.
 * Distinct from the operational `status` (active/inactive) used for rankings.
 * Approval is informational: a `pending` team still participates; admins
 * review registrations and move them to `approved` or `rejected`.
 *
 * @deprecated The lightweight flow replaces approve/reject with league/division
 * assignment by an administrator (see {@link AssignTeamRequest}). Retained for
 * the existing re-registration/review code paths.
 */
export var TeamRegistrationStatus;
(function (TeamRegistrationStatus) {
    TeamRegistrationStatus["PENDING"] = "pending";
    TeamRegistrationStatus["APPROVED"] = "approved";
    TeamRegistrationStatus["REJECTED"] = "rejected";
})(TeamRegistrationStatus || (TeamRegistrationStatus = {}));
/**
 * A team's badminton composition, derived from its type + player genders. A
 * division declares which compositions it accepts (see `Division.eligibleCompositions`),
 * which enables cross-category play (e.g. a women's-doubles team enrolling into a
 * men's-doubles division that accepts it).
 */
export var TeamComposition;
(function (TeamComposition) {
    TeamComposition["MENS_SINGLES"] = "mens-singles";
    TeamComposition["WOMENS_SINGLES"] = "womens-singles";
    TeamComposition["MENS_DOUBLES"] = "mens-doubles";
    TeamComposition["WOMENS_DOUBLES"] = "womens-doubles";
    TeamComposition["MIXED"] = "mixed";
})(TeamComposition || (TeamComposition = {}));
/** Lifecycle of a team's enrollment into a division. */
export var EnrollmentStatus;
(function (EnrollmentStatus) {
    /**
     * Enrolled against a division but with no upcoming season yet — season-less
     * (`seasonId` absent). Waits in the division's queue; converted to
     * `pending_placement` when the next upcoming season is created.
     */
    EnrollmentStatus["QUEUED"] = "queued";
    /** Enrolled into an upcoming season's roster; awaiting the admin placing it into a tier. */
    EnrollmentStatus["PENDING_PLACEMENT"] = "pending_placement";
    /** Placed into a tier and competing. */
    EnrollmentStatus["ACTIVE"] = "active";
    /** Withdrawn from this division. */
    EnrollmentStatus["WITHDRAWN"] = "withdrawn";
    /**
     * The team competed in this division for a season that has since been
     * finalised. A terminal, historical state — no longer a current competitor
     * (so it is excluded from "currently placed" queries, proposable fixtures and
     * live standings), but distinct from `withdrawn` (the team finished the season
     * rather than quitting it). The finalised season's standings live in the
     * season's `standingsSnapshot`.
     */
    EnrollmentStatus["COMPLETED"] = "completed";
})(EnrollmentStatus || (EnrollmentStatus = {}));
// Game types
export var GameStatus;
(function (GameStatus) {
    /**
     * A pre-built fixture (round-robin pairing) generated when the division became
     * active. It has teams but no schedule or result yet, and nobody has proposed it
     * — "Pending to Propose". Proposing claims it (→ PROPOSED) and recording it
     * schedules it directly (→ SCHEDULED), so each pairing exists exactly once and
     * cannot be duplicated.
     */
    GameStatus["PENDING"] = "pending";
    /**
     * The fixture has an open proposal under negotiation — "Pending Proposal". This
     * is the atomic claimed state: a PROPOSED game is off the "to propose" list
     * everywhere. Accepting a slot moves it to SCHEDULED; declining the (last) offer
     * releases it back to PENDING.
     */
    GameStatus["PROPOSED"] = "proposed";
    GameStatus["SCHEDULED"] = "scheduled";
    GameStatus["COMPLETED"] = "completed";
    GameStatus["CANCELLED"] = "cancelled";
    GameStatus["DISPUTED"] = "disputed";
})(GameStatus || (GameStatus = {}));
export var GameProposalStatus;
(function (GameProposalStatus) {
    GameProposalStatus["PENDING"] = "pending";
    GameProposalStatus["ACCEPTED"] = "accepted";
    GameProposalStatus["COUNTERED"] = "countered";
    GameProposalStatus["EXPIRED"] = "expired";
    GameProposalStatus["CANCELLED"] = "cancelled";
    GameProposalStatus["DECLINED"] = "declined";
})(GameProposalStatus || (GameProposalStatus = {}));
// League types
export var LeagueStatus;
(function (LeagueStatus) {
    LeagueStatus["ACTIVE"] = "active";
    LeagueStatus["INACTIVE"] = "inactive";
    LeagueStatus["PLANNING"] = "planning";
    LeagueStatus["COMPLETED"] = "completed";
})(LeagueStatus || (LeagueStatus = {}));
export var SeasonStatus;
(function (SeasonStatus) {
    SeasonStatus["UPCOMING"] = "upcoming";
    SeasonStatus["ACTIVE"] = "active";
    SeasonStatus["COMPLETED"] = "completed";
})(SeasonStatus || (SeasonStatus = {}));
export var DivisionStatus;
(function (DivisionStatus) {
    DivisionStatus["UPCOMING"] = "upcoming";
    DivisionStatus["ACTIVE"] = "active";
    DivisionStatus["COMPLETED"] = "completed";
})(DivisionStatus || (DivisionStatus = {}));
export var Category;
(function (Category) {
    Category["MENS_SINGLES"] = "Men's Singles";
    Category["WOMENS_SINGLES"] = "Women's Singles";
    Category["MENS_DOUBLES"] = "Men's Doubles";
    Category["WOMENS_DOUBLES"] = "Women's Doubles";
    Category["MIXED_DOUBLES"] = "Mixed Doubles";
})(Category || (Category = {}));
// Phase 4: Season Transition types
export var PeriodStatus;
(function (PeriodStatus) {
    PeriodStatus["UPCOMING"] = "upcoming";
    PeriodStatus["ACTIVE"] = "active";
    PeriodStatus["COMPLETED"] = "completed";
})(PeriodStatus || (PeriodStatus = {}));
// Phase 6: Admin & Logging types
export var LogLevel;
(function (LogLevel) {
    LogLevel["ERROR"] = "error";
    LogLevel["WARN"] = "warn";
    LogLevel["INFO"] = "info";
    LogLevel["DEBUG"] = "debug";
})(LogLevel || (LogLevel = {}));
export var AuditAction;
(function (AuditAction) {
    AuditAction["CREATE"] = "create";
    AuditAction["UPDATE"] = "update";
    AuditAction["DELETE"] = "delete";
    AuditAction["LOGIN"] = "login";
    AuditAction["LOGOUT"] = "logout";
    AuditAction["ROLE_CHANGE"] = "role_change";
    AuditAction["CONFIG_CHANGE"] = "config_change";
})(AuditAction || (AuditAction = {}));
export var MetricType;
(function (MetricType) {
    MetricType["GAUGE"] = "gauge";
    MetricType["COUNTER"] = "counter";
    MetricType["HISTOGRAM"] = "histogram";
})(MetricType || (MetricType = {}));
